import {
  BULK_SALES_CHANNELS,
  BULK_SALES_FLAG_KEYS,
  type BulkSalesChannel,
  type BulkSalesChannelDecision,
  type BulkSalesContactRoute,
  type BulkSalesExclusionSource,
  type BulkSalesFlags,
  type BulkSalesTargetState,
  type FirmBulkSalesDecision,
  type LawyerBulkSalesDecision,
} from './model';

/// 一括営業可否の表示ラベル (spec 20260716「Header and List Labels」/「法律事務所詳細の
/// 所属弁護士一覧」)。派生レスポンスを文字列に写すだけの純粋関数で、合成判定は持たない。
/// 表示は「一括営業対象外」系に統一し、「接触停止」「無効」とは表現しない。

export const BULK_SALES_CHANNEL_LABELS: Record<BulkSalesChannel, string> = {
  email: 'メール',
  phone: '電話',
  fax: 'FAX',
  postal_dm: '郵送DM',
  sms: 'SMS',
};

export const BULK_SALES_SOURCE_LABELS: Record<BulkSalesExclusionSource, string> = {
  lawyer: '本人',
  firm: '事務所',
  disciplinary: '懲戒',
};

export const BULK_SALES_STATE_LABELS: Record<BulkSalesTargetState, string> = {
  target: '対象',
  partially_excluded: '一部対象外',
  excluded: '対象外',
};

/// 連絡経路の短縮ラベル。経路で結果が分かれるときに `電話（代表）：事務所` のように
/// チャネル名へ添える。
const BULK_SALES_ROUTE_LABELS: Record<BulkSalesContactRoute, string> = {
  lawyer_emails: '本人メール',
  lawyer_direct_line: '直通',
  firm_email: '事務所メール',
  firm_phone: '代表',
  firm_fax: 'FAX',
  firm_address: '住所',
};

const SOURCE_ORDER: readonly BulkSalesExclusionSource[] = ['lawyer', 'firm', 'disciplinary'];

function joinSources(sources: readonly BulkSalesExclusionSource[]): string {
  return SOURCE_ORDER.filter((s) => sources.includes(s))
    .map((s) => BULK_SALES_SOURCE_LABELS[s])
    .join('・');
}

function joinChannels(channels: readonly BulkSalesChannel[]): string {
  return channels.map((c) => BULK_SALES_CHANNEL_LABELS[c]).join('・');
}

/// 派生値全体に現れる由来の和集合 (固定順)。
export function lawyerBulkSalesSources(
  decision: LawyerBulkSalesDecision,
): BulkSalesExclusionSource[] {
  const present = new Set<BulkSalesExclusionSource>();
  for (const c of BULK_SALES_CHANNELS) {
    for (const s of decision.channels[c].sources) present.add(s);
  }
  return SOURCE_ORDER.filter((s) => present.has(s));
}

/// 除外のあるチャネル (対象外 / 一部対象外) を固定順で返す。
function affectedChannels(decision: LawyerBulkSalesDecision): BulkSalesChannel[] {
  return BULK_SALES_CHANNELS.filter((c) => decision.channels[c].state !== 'target');
}

/// 弁護士のヘッダ・一覧用の集約ラベル。除外なし (対象) は null (バッジを出さない)。
///
/// - 全チャネル対象外: `一括営業対象外（全チャネル・本人）` のように由来を添える
/// - 一部のチャネルが丸ごと対象外: `一括営業対象外（メール・FAX）`
/// - 経路で分かれるチャネルを含む (決定 5): `一括営業一部対象外（メール・電話）`
export function lawyerBulkSalesSummaryLabel(decision: LawyerBulkSalesDecision): string | null {
  if (decision.state === 'target') return null;
  if (decision.state === 'excluded') {
    return `一括営業対象外（全チャネル・${joinSources(lawyerBulkSalesSources(decision))}）`;
  }
  const affected = affectedChannels(decision);
  const allFullyExcluded = affected.every((c) => decision.channels[c].state === 'excluded');
  const prefix = allFullyExcluded ? '一括営業対象外' : '一括営業一部対象外';
  return `${prefix}（${joinChannels(affected)}）`;
}

/// 法律事務所のヘッダ・一覧用の集約ラベル (由来表記なし)。除外なしは null。
export function firmBulkSalesSummaryLabel(decision: FirmBulkSalesDecision): string | null {
  if (decision.state === 'target') return null;
  if (decision.state === 'excluded') return '一括営業対象外（全チャネル）';
  return `一括営業対象外（${joinChannels(decision.excluded_channels)}）`;
}

/// 所属弁護士一覧の 3 段階バッジのツールチップ本文 (見出しを除く行)。
///
/// - 対象: `対象外チャネルなし` の 1 行
/// - チャネル丸ごと対象外: `メール：本人`
/// - 経路で分かれる: `電話（代表）：事務所` (除外された経路ごとに 1 行)
export function lawyerBulkSalesTooltipLines(decision: LawyerBulkSalesDecision): string[] {
  const lines: string[] = [];
  for (const channel of BULK_SALES_CHANNELS) {
    const c: BulkSalesChannelDecision = decision.channels[channel];
    const name = BULK_SALES_CHANNEL_LABELS[channel];
    if (c.state === 'excluded') {
      lines.push(`${name}：${joinSources(c.sources)}`);
    } else if (c.state === 'partially_excluded') {
      for (const r of c.routes) {
        if (!r.excluded) continue;
        lines.push(`${name}（${BULK_SALES_ROUTE_LABELS[r.route]}）：${joinSources(r.sources)}`);
      }
    }
  }
  return lines.length > 0 ? lines : ['対象外チャネルなし'];
}

/// レコード (lawyer / firm) から保存値 5 boolean を取り出す。
export function bulkSalesFlagsOf(record: BulkSalesFlags): BulkSalesFlags {
  return {
    bulk_sales_email_excluded: record.bulk_sales_email_excluded,
    bulk_sales_phone_excluded: record.bulk_sales_phone_excluded,
    bulk_sales_fax_excluded: record.bulk_sales_fax_excluded,
    bulk_sales_postal_dm_excluded: record.bulk_sales_postal_dm_excluded,
    bulk_sales_sms_excluded: record.bulk_sales_sms_excluded,
  };
}

export function allBulkSalesFlagsOn(flags: BulkSalesFlags): boolean {
  return BULK_SALES_CHANNELS.every((c) => flags[BULK_SALES_FLAG_KEYS[c]]);
}

/// 単一の動的一括操作のラベル (2026-07-22 決定 6): 5 つ全 ON のときだけ `すべて解除`、
/// 0〜4 個 ON なら `すべて選択`。操作自体の状態は保存しない。
export function bulkSalesToggleAllLabel(flags: BulkSalesFlags): string {
  return allBulkSalesFlagsOn(flags) ? 'すべて解除' : 'すべて選択';
}

/// 一括操作の PATCH 本文: 5 boolean をまとめて送る (専用フィールドなし)。
export function bulkSalesToggleAllPatch(flags: BulkSalesFlags): BulkSalesFlags {
  const next = !allBulkSalesFlagsOn(flags);
  return {
    bulk_sales_email_excluded: next,
    bulk_sales_phone_excluded: next,
    bulk_sales_fax_excluded: next,
    bulk_sales_postal_dm_excluded: next,
    bulk_sales_sms_excluded: next,
  };
}
